import { defineStore } from "pinia";
import AuthService from "../services/auth.service.ts";
import { newCache } from "../utils/cache";

const THREE_MINS = 3 * 60 * 1000;
const adminCache = newCache(THREE_MINS);

export const useAdminStore = defineStore("admin", {
  state: () => ({
    loggedIn: false,
    user: null,
    userFetchedAt: null,
    serverUpdateDismissed: false,
    fleetOutdatedDismissed: false,
    userLimitExceededDismissed: false,
    incompatibleAgentAlertDismissed: false,
    samlErrorDismissed: false,
    samlCertExpiryDismissed: false,
    publicUrlMismatchDismissed: false,
    // Waved away for this login only. Persisted so a reload does not nag again, and
    // cleared by resetAndClearPersisted on logout, so the next sign-in starts fresh.
    inactiveUsersHintDismissed: false,
  }),

  persist: {
    pick: ["user", "userFetchedAt", "inactiveUsersHintDismissed"],
  },

  actions: {
    async login(userInput) {
      try {
        await AuthService.adminLogin(userInput);
        this.loggedIn = true;
      } catch (err) {
        this.resetAndClearPersisted();
        throw err;
      }
    },

    async fetchStatus(options = {}) {
      try {
        const user = await adminCache({
          state: this,
          key: "user",
          fetchFn: () => AuthService.adminStatus(),
          ...options,
        });
        if (user?.SamlError !== this.user?.SamlError || user?.AdminSamlError !== this.user?.AdminSamlError) {
          this.samlErrorDismissed = false;
        }
        if (
          user?.ConfiguredPublicURL !== this.user?.ConfiguredPublicURL ||
          user?.CurrentURL !== this.user?.CurrentURL
        ) {
          this.publicUrlMismatchDismissed = false;
        }
        this.loggedIn = !!user;
        return user;
      } catch (err) {
        this.resetAndClearPersisted();
        throw err;
      }
    },

    dismissServerUpdate() {
      this.serverUpdateDismissed = true;
    },

    dismissFleetOutdated() {
      this.fleetOutdatedDismissed = true;
    },

    dismissIncompatibleAgentAlert() {
      this.incompatibleAgentAlertDismissed = true;
    },

    dismissUserLimitExceeded() {
      this.userLimitExceededDismissed = true;
    },

    dismissSamlError() {
      this.samlErrorDismissed = true;
    },

    dismissSamlCertExpiry() {
      this.samlCertExpiryDismissed = true;
    },

    dismissPublicUrlMismatch() {
      this.publicUrlMismatchDismissed = true;
    },

    dismissInactiveUsersHint() {
      this.inactiveUsersHintDismissed = true;
    },

    async logout() {
      try {
        await AuthService.adminLogout();
      } finally {
        this.resetAndClearPersisted();
      }
    },

    hasFreshUser() {
      if (!this.user || !this.userFetchedAt) {
        return false;
      }
      return Date.now() - this.userFetchedAt < THREE_MINS;
    },
  },
});
